import { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import { MapContainer, TileLayer, GeoJSON, useMapEvents, useMap } from 'react-leaflet'
import type { Map as LeafletMap } from 'leaflet'
import L from 'leaflet'
import { useQueryClient } from '@tanstack/react-query'
import { LayerControl } from './LayerControl.tsx'
import { TraceOverlay } from './TraceOverlay.tsx'
import { SelectionHalo } from './SelectionHalo.tsx'
import { useSelectedGeometry } from './useSelectedGeometry.ts'
import { SearchHighlightOverlay, type SearchHighlight } from './SearchHighlightOverlay.tsx'
import { DownstreamHighlightOverlay } from './DownstreamHighlightOverlay.tsx'
import { shapeMarker } from './ShapeMarker.ts'
import {
  SUBSTATION_SYMBOL,
  TRANSFORMER_SYMBOL,
  METER_SYMBOL,
  deviceSymbol,
  markerStyle,
} from './symbology.ts'
import { supersedeByMeasured } from './overlayPrecedence.ts'
import type { SearchZoomTarget } from '../search/searchZoom.ts'
import { decideDetailPan } from './panToDetail.ts'
import { DETAIL_MIN_ZOOM, zoomGatedLayerLabels } from './zoomGate.ts'
import type { FullTraceData } from '../../hooks/useTrace.ts'
import {
  useSubstationsLayer,
  useFeedersLayer,
  useTransformersLayer,
  useConductorsLayer,
  useSecondaryLayer,
  useDevicesLayer,
  useMetersLayer,
  useFaultCurrentLayer,
} from '../../hooks/useMapLayers.ts'
import { faultCurrentColor } from '../../utils/faultCurrentStyle.ts'
import { friendlyError } from '../../utils/friendlyError.ts'
import { assignMeter, getMeter } from '../../api/topology.ts'
import type { GeoJSONFeatureCollection, Meter } from '../../types/topology.ts'
import type { BBox } from '../../api/map.ts'
import { detectFeatureType } from '../details/DetailPanel.tsx'
import { useTransformerLoadingGeoJSON, useConductorVoltageDropGeoJSON } from '../../hooks/useEngineering.ts'
import { LOADING_COLORS, VDROP_COLORS } from '../../types/engineering.ts'
import { useMeasuredLoadingGeoJSON, useVoltageViolationsGeoJSON } from '../../hooks/useAmiAnalytics.ts'
import { MEASURED_STATUS_COLORS } from '../../types/amiAnalytics.ts'
import { useSelectedFeeder } from '../../hooks/useSelectedFeeder.ts'
import { tierColor } from '../../types/secondary.ts'
import type { ConnectivityTier } from '../../types/secondary.ts'
import { isApiError } from '../../api/client.ts'
import { useAuth } from '../../hooks/useAuth.ts'
import { useUndoWindow } from '../../hooks/useUndoWindow.ts'
import { hasCapability } from '../../utils/capabilities.ts'

/** Prior linkage captured before a map-mode meter assignment, for the 30 s
 *  undo (UX C4). `assignedTransformerId` is what the reverse write asserts is
 *  still current, so a concurrent re-parent 409s instead of being stomped. */
interface PendingAssignUndo {
  meterId: number
  // Human-readable label, captured for free off the same `prior` read that
  // supplies previousTransformerId — nicer than a bare id in the banner
  // (review Minor 7). Falls back to the id when unavailable.
  meterNumber: string | null
  previousTransformerId: number
  assignedTransformerId: number
}

// Cache Valley, UT center
const DEFAULT_CENTER: [number, number] = [41.7355, -111.8338]
const DEFAULT_ZOOM = 12

// Layer color config matching the original vanilla JS
const COLORS = {
  substation: '#e74c3c',
  feeder: '#3498db',
  transformer: {
    unverified: '#f39c12',
    verified: '#2ecc71',
    flagged: '#e74c3c',
    needs_review: '#9b59b6',
  },
  meter: {
    unverified: '#95a5a6',
    verified: '#2ecc71',
    flagged: '#e74c3c',
    needs_review: '#9b59b6',
  },
  conductor: {
    overhead: '#8B4513',
    underground: '#4B0082',
  },
  secondary: {
    overhead: '#9b59b6',
    underground: '#6c3483',
  },
  device: {
    fuse: '#FF6B6B',
    switch: '#4ECDC4',
    recloser: '#FFE66D',
    sectionalizer: '#95E1D3',
    capacitor: '#DDA0DD',
    normal_open: '#87CEEB',
    switch_46kv: '#4ECDC4',
  },
} as const

interface AssignmentMode {
  active: boolean
  meterId: number | null
}

interface MapViewProps {
  onFeatureSelect: (feature: Record<string, unknown> | null) => void
  selectedFeature: Record<string, unknown> | null
  onDeselect: () => void
  searchHighlight: SearchHighlight | null
  notice: string | null
  assignmentMode: AssignmentMode
  onAssignmentComplete: () => void
  zoomTarget: SearchZoomTarget | null
  onZoomHandled: () => void
  onMouseCoords: (coords: { lat: number; lng: number } | null) => void
  traceData: FullTraceData | null
  onClearTrace: () => void
  downstreamHighlight: import('../../types/topology.ts').GeoJSONFeatureCollection<import('../../types/topology.ts').GeoJSONPoint> | null
  onClearDownstream: () => void
  /** Coordinates of the selected detail entity (e.g. a meter chosen from the
   *  transformer panel). Draws a selection halo independent of layer toggles. */
  detailHighlight: { lat: number; lng: number } | null
  /** Pan request from a detail selection / the "Show on map" button. */
  panRequest: { lat: number; lng: number; force: boolean; token: number } | null
  onPanHandled: () => void
}

function BoundsTracker({
  onBoundsChange,
  onZoomChange,
}: {
  onBoundsChange: (bbox: BBox) => void
  onZoomChange: (zoom: number) => void
}) {
  const map = useMapEvents({
    moveend() {
      const bounds = map.getBounds()
      onBoundsChange({
        minLon: bounds.getWest(),
        minLat: bounds.getSouth(),
        maxLon: bounds.getEast(),
        maxLat: bounds.getNorth(),
      })
      onZoomChange(map.getZoom())
    },
  })
  useEffect(() => {
    const bounds = map.getBounds()
    onBoundsChange({
      minLon: bounds.getWest(),
      minLat: bounds.getSouth(),
      maxLon: bounds.getEast(),
      maxLat: bounds.getNorth(),
    })
    onZoomChange(map.getZoom())
  }, [map, onBoundsChange, onZoomChange])
  return null
}

function MouseTracker({ onMove }: { onMove: (coords: { lat: number; lng: number } | null) => void }) {
  useMapEvents({
    mousemove(e) {
      onMove({ lat: e.latlng.lat, lng: e.latlng.lng })
    },
    mouseout() {
      onMove(null)
    },
  })
  return null
}

function FlyToHandler({ target, onDone }: { target: SearchZoomTarget | null; onDone: () => void }) {
  const map = useMap()
  useEffect(() => {
    if (target) {
      if (target.kind === 'bounds') {
        map.fitBounds(L.latLngBounds(target.bounds), { padding: [40, 40] })
      } else {
        map.flyTo([target.lat, target.lng], target.zoom, { duration: 0.8 })
      }
      onDone()
    }
  }, [target, map, onDone])
  return null
}

/**
 * Pans/flies the map toward a selected detail entity (a meter opened from the
 * transformer panel, or the "Show on map" button). Keeps the current zoom when
 * the point is already visible; flies in when it is off-screen. Runs on the
 * request token so a repeat "Show on map" re-fires, then clears the request.
 */
function PanToHandler({
  request,
  onDone,
}: {
  request: { lat: number; lng: number; force: boolean; token: number } | null
  onDone: () => void
}) {
  const map = useMap()
  useEffect(() => {
    if (!request) return
    const { lat, lng, force } = request
    const bounds = map.getBounds()
    const decision = decideDetailPan(
      { lat, lng },
      { contains: (la, ln) => bounds.contains(L.latLng(la, ln)), zoom: map.getZoom() },
      force,
    )
    if (decision.action === 'pan') {
      map.panTo([lat, lng], { animate: true, duration: 0.6 })
    } else if (decision.action === 'fly') {
      map.flyTo([lat, lng], decision.zoom, { duration: 0.6 })
    }
    onDone()
  }, [request, map, onDone])
  return null
}

/**
 * Clears the current selection when the basemap (not a feature) is clicked.
 * Feature click handlers call L.DomEvent.stopPropagation, so a map-level
 * click only fires for true background clicks. Never clears the trace.
 */
function DeselectOnMapClick({ onDeselect }: { onDeselect: () => void }) {
  useMapEvents({
    click() {
      onDeselect()
    },
  })
  return null
}

export function MapView({
  onFeatureSelect,
  selectedFeature,
  onDeselect,
  searchHighlight,
  notice,
  assignmentMode,
  onAssignmentComplete,
  zoomTarget,
  onZoomHandled,
  onMouseCoords,
  traceData,
  onClearTrace,
  downstreamHighlight,
  onClearDownstream,
  detailHighlight,
  panRequest,
  onPanHandled,
}: MapViewProps) {
  const mapRef = useRef<LeafletMap | null>(null)
  const queryClient = useQueryClient()
  const [visibleLayers, setVisibleLayers] = useState<Record<string, boolean>>({
    substations: true,
    feeders: true,
    transformers: true,
    meters: false,
    conductors: false,
    secondary: false,
    devices: false,
    loading: false,
    voltageDrop: false,
    faultCurrent: false,
    measuredLoading: false,
    voltageViolations: false,
  })
  const [bbox, setBbox] = useState<BBox | undefined>(undefined)
  const [zoom, setZoom] = useState<number>(DEFAULT_ZOOM)
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined)
  // Secondary color-by-confidence mode (off => plain OH/UG style). Requests the
  // include_connectivity flag so each feature carries derived tier props.
  const [secondaryColorByConfidence, setSecondaryColorByConfidence] = useState(false)
  const [assignError, setAssignError] = useState<string | null>(null)
  // 30 s undo after a successful map-mode meter assignment (UX C4). Renders
  // only for gis.override holders — the reverse assignment is itself
  // capability-gated server-side.
  const { user } = useAuth()
  const { pending: pendingUndo, arm: armUndo, clear: clearUndo } =
    useUndoWindow<PendingAssignUndo>()
  // In-flight guard on the reverse write (review Important 2): without this a
  // double-click fires two reverse assigns, and once Task 9.9's precondition
  // is live the second one 409s against the first's own success — a false
  // "someone else changed this" for a conflict that never happened.
  const [undoBusy, setUndoBusy] = useState(false)
  // Cross-view feeder selection (shared with the Engineering dashboard).
  const { selectedFeederId, setSelectedFeederId } = useSelectedFeeder()
  // Selected conductor id for line styling (feeders key off selectedFeederId).
  const selectedConductorId =
    selectedFeature &&
    detectFeatureType(selectedFeature) === 'Conductor' &&
    typeof selectedFeature.id === 'number'
      ? (selectedFeature.id as number)
      : null
  // Selected secondary segment id — secondary clicks stamp _type 'Secondary'
  // (which short-circuits detectFeatureType before the 'Conductor' inference,
  // so this stays distinct from selectedConductorId above).
  const selectedSecondaryId =
    selectedFeature &&
    selectedFeature._type === 'Secondary' &&
    typeof selectedFeature.id === 'number'
      ? (selectedFeature.id as number)
      : null

  // Stable version counters — only increment when data actually changes
  const [dataVersions, setDataVersions] = useState({
    substations: 0,
    feeders: 0,
    transformers: 0,
    meters: 0,
    conductors: 0,
    secondary: 0,
    devices: 0,
  })

  // Heavy detail layers (transformers, meters, devices, secondary) are
  // zoom-gated (spec D-9): below DETAIL_MIN_ZOOM they neither fetch nor
  // render, and the "Zoom in to load …" banner shows instead. Search
  // fly-to zooms (16–18) always land above the gate.
  const detailZoomOk = zoom >= DETAIL_MIN_ZOOM

  // Data queries — pass status filter to transformer and meter layers
  const { data: substations, isLoading: loadingSubstations } = useSubstationsLayer()
  const { data: feeders, isLoading: loadingFeeders } = useFeedersLayer()
  const { data: transformers, isLoading: loadingTransformers } = useTransformersLayer(
    { status: statusFilter },
    { enabled: visibleLayers.transformers && detailZoomOk },
  )
  const { data: meters, isLoading: loadingMeters } = useMetersLayer({
    bbox: visibleLayers.meters && detailZoomOk ? bbox : undefined,
    status: statusFilter,
  })
  const { data: conductors, isLoading: loadingConductors } = useConductorsLayer()
  // Secondary is bbox + zoom gated (large layer): only fetch when toggled on and
  // zoomed into a working area.
  const secondaryEnabled = visibleLayers.secondary && detailZoomOk
  const { data: secondary, isLoading: loadingSecondary } = useSecondaryLayer({
    bbox: secondaryEnabled ? bbox : undefined,
    include_connectivity: secondaryColorByConfidence,
  })
  const { data: devices, isLoading: loadingDevices } = useDevicesLayer(undefined, {
    enabled: visibleLayers.devices && detailZoomOk,
  })

  // Engineering layers. The Loading layer defaults to the MEASURED basis with
  // per-transformer model fallback, matching the dashboard default, so a map
  // dot can never contradict the detail panel it opens (spec §3.4).
  const { data: loadingLayer, isLoading: loadingLoadingLayer } = useTransformerLoadingGeoJSON(
    visibleLayers.loading ? { load_source: 'measured' } : undefined,
  )
  // Voltage drop requires a feeder selection
  const { data: vdropLayer, isLoading: loadingVdropLayer } = useConductorVoltageDropGeoJSON(
    visibleLayers.voltageDrop && selectedFeederId
      ? { feeder_id: selectedFeederId }
      : null,
  )
  // Fault current is feeder-scoped too (per-feeder fault context). INDICATIVE
  // until utility source data is loaded — the stamp rides on each feature.
  const { data: faultLayer, isLoading: loadingFaultLayer } = useFaultCurrentLayer(
    visibleLayers.faultCurrent && selectedFeederId ? selectedFeederId : null,
  )

  // AMI layers
  const { data: measuredLoadingLayer, isLoading: loadingMeasuredLoadingLayer } = useMeasuredLoadingGeoJSON(
    visibleLayers.measuredLoading ? {} : undefined,
  )
  const { data: violationsLayer, isLoading: loadingViolationsLayer } = useVoltageViolationsGeoJSON(
    visibleLayers.voltageViolations ? {} : undefined,
  )

  // Measured AMI supersedes the flat model estimate where both cover the same
  // bank; transformers with no AMI keep their estimated triangle because only
  // the loading layer has a feature for them. Resolved in data, not draw order
  // — canvas draw order follows _initPath append order (Canvas._initPath
  // appends to _drawLast; leaflet-src.js:12703-12713), which changes on layer
  // remount.
  //
  // useMemo (not a plain call) so the result keeps a stable identity across
  // re-renders that don't change any of the three inputs. supersedeByMeasured
  // returns a fresh spread object on every invocation when the filter runs,
  // so without memoizing, the version-bump effect below (which must depend on
  // this value to remount the rendered layer — react-leaflet's <GeoJSON>
  // never re-applies a changed `data` prop) would see a "changed" dependency
  // on every render and loop. Memoizing makes the reference change only when
  // loadingLayer, measuredLoadingLayer, or the measuredLoading toggle
  // actually changes, so the effect fires exactly once per real data change.
  const effectiveLoadingLayer = useMemo(
    () => supersedeByMeasured(loadingLayer, measuredLoadingLayer, visibleLayers.measuredLoading),
    [loadingLayer, measuredLoadingLayer, visibleLayers.measuredLoading],
  )

  // Increment version counters when data changes
  useEffect(() => { if (substations) setDataVersions(v => ({ ...v, substations: v.substations + 1 })) }, [substations])
  useEffect(() => { if (feeders) setDataVersions(v => ({ ...v, feeders: v.feeders + 1 })) }, [feeders])
  useEffect(() => { if (transformers) setDataVersions(v => ({ ...v, transformers: v.transformers + 1 })) }, [transformers])
  useEffect(() => { if (meters) setDataVersions(v => ({ ...v, meters: v.meters + 1 })) }, [meters])
  useEffect(() => { if (conductors) setDataVersions(v => ({ ...v, conductors: v.conductors + 1 })) }, [conductors])
  useEffect(() => { if (secondary) setDataVersions(v => ({ ...v, secondary: v.secondary + 1 })) }, [secondary])
  useEffect(() => { if (devices) setDataVersions(v => ({ ...v, devices: v.devices + 1 })) }, [devices])

  // Engineering layer versions
  const [engVersions, setEngVersions] = useState({ loading: 0, voltageDrop: 0, faultCurrent: 0, measuredLoading: 0, voltageViolations: 0 })
  // Keyed on effectiveLoadingLayer (not the raw loadingLayer) so the rendered
  // `loading` <GeoJSON> — which uses this counter in its remount key — picks
  // up the filtered data whenever any of the three precedence inputs change,
  // not just when loadingLayer itself refetches.
  useEffect(() => { if (effectiveLoadingLayer) setEngVersions(v => ({ ...v, loading: v.loading + 1 })) }, [effectiveLoadingLayer])
  useEffect(() => { if (vdropLayer) setEngVersions(v => ({ ...v, voltageDrop: v.voltageDrop + 1 })) }, [vdropLayer])
  useEffect(() => { if (faultLayer) setEngVersions(v => ({ ...v, faultCurrent: v.faultCurrent + 1 })) }, [faultLayer])
  useEffect(() => { if (measuredLoadingLayer) setEngVersions(v => ({ ...v, measuredLoading: v.measuredLoading + 1 })) }, [measuredLoadingLayer])
  useEffect(() => { if (violationsLayer) setEngVersions(v => ({ ...v, voltageViolations: v.voltageViolations + 1 })) }, [violationsLayer])

  const handleBoundsChange = useCallback((newBbox: BBox) => {
    setBbox(newBbox)
  }, [])

  const handleZoomChange = useCallback((newZoom: number) => {
    setZoom(newZoom)
  }, [])

  const toggleLayer = useCallback((layer: string) => {
    setVisibleLayers(prev => ({ ...prev, [layer]: !prev[layer] }))
  }, [])

  // A search-picked result must be visible: if its layer is toggled off,
  // enable it (meters default off — searching a meter would otherwise fly
  // to an empty patch of map). The meters layer is bbox-gated, so its data
  // loads after the flyTo triggers moveend/BoundsTracker.
  useEffect(() => {
    if (!selectedFeature || !selectedFeature._searchResult) return
    const layerByType: Record<string, string> = {
      Meter: 'meters',
      Transformer: 'transformers',
      Feeder: 'feeders',
      Substation: 'substations',
    }
    const layerKey = layerByType[selectedFeature._type as string]
    if (!layerKey) return
    setVisibleLayers(prev => (prev[layerKey] ? prev : { ...prev, [layerKey]: true }))
  }, [selectedFeature])

  // Feature click handler — supports assignment mode
  const handleFeatureClick = useCallback(async (props: Record<string, unknown>) => {
    if (assignmentMode.active && assignmentMode.meterId) {
      const clickedType = detectFeatureType(props)
      if (clickedType === 'Transformer' && props.id) {
        setAssignError(null)
        try {
          // Capture the prior transformer BEFORE the write so the 30 s undo
          // can issue the reverse assignment (UX C4). This read must not gate
          // the primary write: if it fails, fall back to no prior state and
          // proceed with the assignment — just without an undo offer (review
          // Important 5).
          let prior: Meter | null = null
          try {
            prior = await getMeter(assignmentMode.meterId)
          } catch (priorErr) {
            // Swallowed on purpose (the primary write must proceed regardless),
            // but not silently — log it for the engineer so a pattern of
            // failed prior-reads (meaning undo quietly stops being offered) is
            // discoverable instead of invisible.
            console.error('Failed to read prior meter state before assign:', priorErr)
            prior = null
          }
          await assignMeter(assignmentMode.meterId, props.id as number)
          await queryClient.invalidateQueries({ queryKey: ['map'] })
          await queryClient.invalidateQueries({ queryKey: ['topology-stats'] })
          // The meter just gained a transformer — drop it from the global
          // unlinked work queue so the VerificationProgressCard count stays current.
          await queryClient.invalidateQueries({ queryKey: ['unlinked-meters'] })
          await queryClient.invalidateQueries({ queryKey: ['meter', assignmentMode.meterId] })
          if (prior?.transformer && prior.transformer.id !== (props.id as number)) {
            armUndo({
              meterId: assignmentMode.meterId,
              meterNumber: prior.meter_number ?? null,
              previousTransformerId: prior.transformer.id,
              assignedTransformerId: props.id as number,
            })
          } else {
            // First-time assignment (no prior transformer), a no-op re-pick,
            // or the prior-state read failed — drop any stale undo banner
            // left over from a different meter (review Important 3; mirrors
            // TransformerDetail's handleAssignFeeder else-clear).
            clearUndo()
          }
          onAssignmentComplete()
        } catch (err) {
          // Task 8.9 (A8) converted this site to friendlyError — keep it.
          setAssignError(friendlyError(err, 'assign the meter'))
        }
        return
      }
    }
    // Track feeder selection for voltage drop layer
    const clickedType = detectFeatureType(props)
    if (clickedType === 'Feeder' && props.id) {
      setSelectedFeederId(props.id as number)
    }
    onFeatureSelect(props)
  }, [assignmentMode, onFeatureSelect, onAssignmentComplete, queryClient, setSelectedFeederId, armUndo, clearUndo])

  // Take the 30 s undo: reverse the assignment (a normal audited reassign).
  // The undo state is cleared only AFTER the write settles — clearing first is
  // what made a failed undo invisible (banner gone, error in a slot that only
  // renders during assignment mode).
  const handleUndoAssign = useCallback(async () => {
    const pending = pendingUndo
    // In-flight guard (review Important 2): without this, a double-click (or
    // a slow first request plus an impatient second click) fires two reverse
    // assigns — the second one either duplicates the write or, once Task 9.9's
    // precondition is live, 409s against the first undo's own success, which
    // would misreport as "someone else changed this" for a conflict that
    // never happened.
    if (!pending || undoBusy) return
    setUndoBusy(true)
    setAssignError(null)
    try {
      await assignMeter(pending.meterId, pending.previousTransformerId, {
        // Only reverse if the meter is still where we just put it.
        expectedCurrentTransformerId: pending.assignedTransformerId,
      })
      await queryClient.invalidateQueries({ queryKey: ['map'] })
      await queryClient.invalidateQueries({ queryKey: ['topology-stats'] })
      await queryClient.invalidateQueries({ queryKey: ['unlinked-meters'] })
      await queryClient.invalidateQueries({ queryKey: ['meter', pending.meterId] })
      clearUndo()
    } catch (err) {
      // Spend the offer, but surface the failure in the banner's own slot.
      clearUndo()
      setAssignError(
        isApiError(err) && err.status === 409
          ? 'Someone else changed this since — refresh to see the current assignment.'
          : friendlyError(err, 'undo the assignment'),
      )
    } finally {
      setUndoBusy(false)
    }
  }, [pendingUndo, undoBusy, clearUndo, queryClient])

  // Memoized style functions
  const substationPointToLayer = useCallback((_: GeoJSON.Feature, latlng: L.LatLng) => {
    return shapeMarker(latlng, {
      shape: SUBSTATION_SYMBOL.shape,
      radius: SUBSTATION_SYMBOL.size,
      fillColor: COLORS.substation,
      color: '#fff',
      weight: 2,
      fillOpacity: 0.9,
    })
  }, [])

  // Selected feeder gets a visible bump so the voltage-drop "click a feeder"
  // flow confirms which feeder is active (amber matches that prompt banner).
  // selectedFeederId in the deps => react-leaflet re-applies setStyle when the
  // selection changes; version-keyed remounts re-run the callback with the
  // current selection closed over.
  const feederStyle = useCallback((feature?: GeoJSON.Feature) => {
    const isSelected = selectedFeederId != null && feature?.properties?.id === selectedFeederId
    return {
      color: isSelected ? '#f59e0b' : COLORS.feeder,
      weight: isSelected ? 6 : 3,
      opacity: isSelected ? 1 : 0.8,
    }
  }, [selectedFeederId])

  const transformerPointToLayer = useCallback((feature: GeoJSON.Feature, latlng: L.LatLng) => {
    const status = (feature.properties?.verification_status as string) || 'unverified'
    const color = COLORS.transformer[status as keyof typeof COLORS.transformer] || COLORS.transformer.unverified
    return shapeMarker(latlng, {
      shape: TRANSFORMER_SYMBOL.shape,
      radius: TRANSFORMER_SYMBOL.size,
      fillColor: color,
      color: '#fff',
      weight: 1,
      fillOpacity: 0.8,
    })
  }, [])

  const meterPointToLayer = useCallback((feature: GeoJSON.Feature, latlng: L.LatLng) => {
    const status = (feature.properties?.verification_status as string) || 'unverified'
    const color = COLORS.meter[status as keyof typeof COLORS.meter] || COLORS.meter.unverified
    return L.circleMarker(latlng, {
      radius: METER_SYMBOL.size,
      fillColor: color,
      color: '#333',
      weight: 0.5,
      fillOpacity: 0.7,
    })
  }, [])

  const conductorStyle = useCallback((feature?: GeoJSON.Feature) => {
    const segType = feature?.properties?.segment_type as string
    const isSelected =
      selectedConductorId != null && feature?.properties?.id === selectedConductorId
    return {
      color: isSelected
        ? '#f59e0b'
        : segType === 'underground' ? COLORS.conductor.underground : COLORS.conductor.overhead,
      weight: isSelected ? 5 : 2,
      opacity: isSelected ? 1 : 0.7,
      dashArray: segType === 'underground' ? '5, 5' : undefined,
    }
  }, [selectedConductorId])

  // Secondary conductors — distinct purple palette and thinner weight to set
  // them apart from primary conductors; UG dashed, OH solid. In color-by-
  // confidence mode the stroke color comes from the derived rooting tier
  // (auto=green / flagged=amber / review=red / no-data=gray) while the OH/UG
  // dash pattern is preserved.
  const secondaryStyle = useCallback((feature?: GeoJSON.Feature) => {
    const segType = feature?.properties?.segment_type as string
    const dashArray = segType === 'underground' ? '4, 4' : undefined
    // Selected segment highlights amber (matches primary conductors), keeping
    // the OH/UG dash pattern, regardless of the color-by-confidence mode.
    const isSelected =
      selectedSecondaryId != null && feature?.properties?.id === selectedSecondaryId
    if (isSelected) {
      return { color: '#f59e0b', weight: 5, opacity: 1, dashArray }
    }
    if (secondaryColorByConfidence) {
      const tier = feature?.properties?.tier as ConnectivityTier | null | undefined
      return {
        color: tierColor(tier),
        weight: 2,
        opacity: 0.85,
        dashArray,
      }
    }
    return {
      color: segType === 'underground' ? COLORS.secondary.underground : COLORS.secondary.overhead,
      weight: 1.5,
      opacity: 0.75,
      dashArray,
    }
  }, [secondaryColorByConfidence, selectedSecondaryId])

  const devicePointToLayer = useCallback((feature: GeoJSON.Feature, latlng: L.LatLng) => {
    const dtype = (feature.properties?.device_type as string) || 'fuse'
    const color = COLORS.device[dtype as keyof typeof COLORS.device] || COLORS.device.fuse
    // normal_status is the map's open/closed source; is_open exists only on
    // the trace payload.
    const normalStatus = feature.properties?.normal_status as string | null | undefined
    const symbol = deviceSymbol(dtype, normalStatus)
    return shapeMarker(latlng, {
      shape: symbol.shape,
      radius: symbol.size,
      ...markerStyle(color, symbol.hollow),
    })
  }, [])

  // Engineering layer styles. Loading draws the SAME triangle at the SAME size
  // as the base transformer layer, so the overlay reads as one symbol whose
  // color changed — which is what the data says — rather than a second asset.
  const loadingPointToLayer = useCallback((feature: GeoJSON.Feature, latlng: L.LatLng) => {
    const status = (feature.properties?.loading_status as string) || 'no_rating'
    const color = LOADING_COLORS[status] || LOADING_COLORS.no_rating
    return shapeMarker(latlng, {
      shape: TRANSFORMER_SYMBOL.shape,
      radius: TRANSFORMER_SYMBOL.size,
      fillColor: color,
      color: '#fff',
      weight: 1.5,
      fillOpacity: 1,
    })
  }, [])

  const vdropStyle = useCallback((feature?: GeoJSON.Feature) => {
    const status = (feature?.properties?.drop_status as string) || 'no_data'
    const color = VDROP_COLORS[status] || VDROP_COLORS.no_data
    return {
      color,
      weight: 3,
      opacity: 0.85,
    }
  }, [])

  const faultCurrentStyle = useCallback((feature?: GeoJSON.Feature) => ({
    color: faultCurrentColor(feature?.properties?.fault_current_3ph_ka as number | null),
    weight: 4,
    opacity: 0.85,
  }), [])

  const measuredLoadingPointToLayer = useCallback((feature: GeoJSON.Feature, latlng: L.LatLng) => {
    const status = (feature.properties?.loading_status as string) || 'no_rating'
    const color = (MEASURED_STATUS_COLORS as Record<string, string>)[status] || MEASURED_STATUS_COLORS.no_rating
    return shapeMarker(latlng, {
      shape: TRANSFORMER_SYMBOL.shape,
      radius: TRANSFORMER_SYMBOL.size,
      fillColor: color,
      color: '#fff',
      weight: 1.5,
      fillOpacity: 1,
    })
  }, [])

  // Voltage violations are METER points (_type: "Meter"), not transformers.
  // Drawn as a translucent alarm disc so the gray base meter dot stays visible
  // at its center — the alarm is a property of the meter's measurements, not a
  // different identity for the asset. The data-driven radius is severity, the
  // one documented exception to size-encodes-hierarchy.
  const violationsPointToLayer = useCallback((feature: GeoJSON.Feature, latlng: L.LatLng) => {
    const rate = (feature.properties?.violation_rate_pct as number) || 0
    // Stays L.circleMarker: a circle is already the meter symbol, so routing
    // it through ShapeMarker would add indirection for no visual change.
    return L.circleMarker(latlng, {
      radius: Math.min(4 + rate, 12),
      fillColor: '#dc2626',
      color: '#fff',
      weight: 1.5,
      fillOpacity: 0.35,
    })
  }, [])

  const onEachFeature = useCallback((feature: GeoJSON.Feature, layer: L.Layer) => {
    layer.on('click', (e: L.LeafletMouseEvent) => {
      // Keep the click from also firing the map-level deselect handler.
      L.DomEvent.stopPropagation(e)
      if (feature.properties) {
        handleFeatureClick({
          ...(feature.properties as Record<string, unknown>),
          _geometry: feature.geometry,
        })
      }
    })
  }, [handleFeatureClick])

  // Secondary lines share `segment_type` with primary conductors, so stamp an
  // explicit feature type on click for the detail panel to route correctly.
  const onEachSecondaryFeature = useCallback((feature: GeoJSON.Feature, layer: L.Layer) => {
    layer.on('click', (e: L.LeafletMouseEvent) => {
      L.DomEvent.stopPropagation(e)
      if (feature.properties) {
        handleFeatureClick({
          ...feature.properties,
          _type: 'Secondary',
          _geometry: feature.geometry,
        } as Record<string, unknown>)
      }
    })
  }, [handleFeatureClick])

  // Geometry driving the blue selection halo (see useSelectedGeometry). The
  // memo keys on the coordinate primitives so the halo reference stays stable
  // across re-renders that don't move the point (e.g. mousemove via
  // onMouseCoords) — SelectionHalo's effect must not tear down/rebuild its
  // layerGroup and restart its pulse on every render.
  const clickGeometry = (selectedFeature?._geometry as GeoJSON.Geometry | undefined) ?? null
  const selectedGeometry = useSelectedGeometry(
    clickGeometry,
    detailHighlight,
    !!searchHighlight,
  )

  // The fault layer stamps fault_indicative_only on every feature (one build,
  // one flag). Read the first feature to drive the INDICATIVE sub-note.
  const faultIndicative =
    (faultLayer as GeoJSONFeatureCollection)?.features?.[0]?.properties?.fault_indicative_only === true

  // Toggled-on heavy layers currently suppressed by the zoom gate (banner copy).
  const zoomGateHint = zoomGatedLayerLabels(zoom, visibleLayers)

  // Fix (review, Important): under preferCanvas, draw order is NOT JSX order
  // — it is the order layers were appended to the canvas renderer's linked
  // list (Canvas._initPath appends to _drawLast; leaflet-src.js:12703-12713).
  // The transformers layer is zoom-gated (visibleLayers.transformers &&
  // detailZoomOk) while the loading/measuredLoading overlays are not, so any
  // transformers remount — toggling the Transformers checkbox off/on,
  // crossing the DETAIL_MIN_ZOOM gate, or a transformers refetch (status
  // filter, or the ['map'] invalidation after a meter assignment) — re-appends
  // it AFTER the overlays and paints the base 80%-alpha triangle back on top,
  // fully hiding the overlay it was supposed to replace (they are drawn
  // pixel-identical on purpose — see loadingPointToLayer). Baking the
  // transformers layer's full mount identity into the loading/measuredLoading
  // keys forces those overlays to remount — and re-append themselves after
  // transformers — whenever transformers does. Do NOT simplify this away: it
  // looks redundant with the transformers layer's own key, but it is the only
  // thing keeping the overlays on top after a remount.
  //
  // `!!transformers` is load-bearing, not defensive. The other three signals
  // flip synchronously, but dataVersions.transformers only bumps a render
  // LATER (its useEffect runs after the data lands). Without the data presence
  // in the identity, a cold-cache zoom past DETAIL_MIN_ZOOM would re-append
  // the overlay on the render where detailZoomOk flips, then mount
  // transformers on top of it on the NEXT render — a one-frame flash of the
  // exact bug this guards against. Keying on the same condition the
  // transformers <GeoJSON> mounts under closes that window.
  const transformersMountIdentity =
    `${dataVersions.transformers}-${visibleLayers.transformers}-${detailZoomOk}-${!!transformers}`

  return (
    <div className={`relative flex-1 ${assignmentMode.active ? 'cursor-crosshair' : ''}`}>
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        minZoom={10}
        maxZoom={19}
        preferCanvas
        className={`h-full w-full ${traceData ? 'trace-active' : ''}`}
        ref={mapRef}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <BoundsTracker onBoundsChange={handleBoundsChange} onZoomChange={handleZoomChange} />
        <MouseTracker onMove={onMouseCoords} />
        <FlyToHandler target={zoomTarget} onDone={onZoomHandled} />
        <PanToHandler request={panRequest} onDone={onPanHandled} />

        {/* Trace overlay (v2 — real path + device diamonds + navigable chips) */}
        {traceData && (
          <TraceOverlay data={traceData} onClear={onClearTrace} onNavigate={onFeatureSelect} />
        )}

        {/* Downstream members highlight (spec §4.3 "show everything fed by …") */}
        <DownstreamHighlightOverlay members={downstreamHighlight} />

        {/* Selection halo for the clicked point feature */}
        <SelectionHalo geometry={selectedGeometry} />

        {/* Search-result highlight (pulsing ring / route underlay + label) */}
        {searchHighlight && <SearchHighlightOverlay highlight={searchHighlight} />}

        {/* Basemap click (not on a feature) clears the selection */}
        <DeselectOnMapClick onDeselect={onDeselect} />

        {/* Conductors (bottom layer) */}
        {visibleLayers.conductors && conductors && (
          <GeoJSON
            key={`conductors-v${dataVersions.conductors}`}
            data={conductors as GeoJSON.GeoJsonObject}
            style={conductorStyle}
            onEachFeature={onEachFeature}
          />
        )}

        {/* Secondary conductors (bbox + zoom gated; large layer). The color
            mode is part of the key so Leaflet remounts and re-applies the
            tier-based style when it toggles. */}
        {visibleLayers.secondary && secondaryEnabled && secondary && (
          <GeoJSON
            key={`secondary-v${dataVersions.secondary}-${secondaryColorByConfidence ? 'conf' : 'plain'}`}
            data={secondary as GeoJSON.GeoJsonObject}
            style={secondaryStyle}
            onEachFeature={onEachSecondaryFeature}
          />
        )}

        {/* Feeders */}
        {visibleLayers.feeders && feeders && (
          <GeoJSON
            key={`feeders-v${dataVersions.feeders}-sel${selectedFeederId ?? 'none'}`}
            data={feeders as GeoJSON.GeoJsonObject}
            style={feederStyle}
            onEachFeature={onEachFeature}
          />
        )}

        {/* AMI: Voltage violations — meter alarm discs, mounted BEFORE meters
            so the base meter dot stays visible at each disc's center. This
            JSX position is not what guarantees that, though: it is subject to
            the same canvas remount/reorder instability as the transformers/
            loading case above. What actually guarantees the meter dot shows
            through is violationsPointToLayer's `fillOpacity: 0.35` — a
            translucent disc lets the dot underneath show regardless of which
            layer painted last. */}
        {visibleLayers.voltageViolations && violationsLayer && (
          <GeoJSON
            key={`voltageViolations-v${engVersions.voltageViolations}`}
            data={violationsLayer as GeoJSON.GeoJsonObject}
            pointToLayer={violationsPointToLayer}
            onEachFeature={onEachFeature}
          />
        )}

        {/* Meters (zoom-gated, spec D-9) */}
        {visibleLayers.meters && detailZoomOk && meters && (
          <GeoJSON
            key={`meters-v${dataVersions.meters}`}
            data={meters as GeoJSON.GeoJsonObject}
            pointToLayer={meterPointToLayer}
            onEachFeature={onEachFeature}
          />
        )}

        {/* Transformers (zoom-gated, spec D-9) */}
        {visibleLayers.transformers && detailZoomOk && transformers && (
          <GeoJSON
            key={`transformers-v${dataVersions.transformers}`}
            data={transformers as GeoJSON.GeoJsonObject}
            pointToLayer={transformerPointToLayer}
            onEachFeature={onEachFeature}
          />
        )}

        {/* Protective Devices (zoom-gated, spec D-9) */}
        {visibleLayers.devices && detailZoomOk && devices && (
          <GeoJSON
            key={`devices-v${dataVersions.devices}`}
            data={devices as GeoJSON.GeoJsonObject}
            pointToLayer={devicePointToLayer}
            onEachFeature={onEachFeature}
          />
        )}

        {/* Engineering: Voltage Drop layer */}
        {visibleLayers.voltageDrop && vdropLayer && (
          <GeoJSON
            key={`vdrop-v${engVersions.voltageDrop}`}
            data={vdropLayer as GeoJSON.GeoJsonObject}
            style={vdropStyle}
            onEachFeature={onEachFeature}
          />
        )}

        {/* Engineering: Fault Current layer (feeder-scoped, kA-colored) */}
        {visibleLayers.faultCurrent && faultLayer && (
          <GeoJSON
            key={`faultCurrent-v${engVersions.faultCurrent}`}
            data={faultLayer as GeoJSON.GeoJsonObject}
            style={faultCurrentStyle}
            onEachFeature={onEachFeature}
          />
        )}

        {/* Engineering: Loading layer (replaces transformer styling). The key
            carries transformersMountIdentity so this overlay remounts (and
            re-appends after transformers in the canvas draw order) whenever
            the transformers layer does — see the comment above that constant. */}
        {visibleLayers.loading && effectiveLoadingLayer && (
          <GeoJSON
            key={`loading-v${engVersions.loading}-tx${transformersMountIdentity}`}
            data={effectiveLoadingLayer as GeoJSON.GeoJsonObject}
            pointToLayer={loadingPointToLayer}
            onEachFeature={onEachFeature}
          />
        )}

        {/* AMI: Measured loading layer. Same transformersMountIdentity guard
            as the loading layer above, for the same reason. */}
        {visibleLayers.measuredLoading && measuredLoadingLayer && (
          <GeoJSON
            key={`measuredLoading-v${engVersions.measuredLoading}-tx${transformersMountIdentity}`}
            data={measuredLoadingLayer as GeoJSON.GeoJsonObject}
            pointToLayer={measuredLoadingPointToLayer}
            onEachFeature={onEachFeature}
          />
        )}

        {/* Substations (top layer) */}
        {visibleLayers.substations && substations && (
          <GeoJSON
            key={`substations-v${dataVersions.substations}`}
            data={substations as GeoJSON.GeoJsonObject}
            pointToLayer={substationPointToLayer}
            onEachFeature={onEachFeature}
          />
        )}
      </MapContainer>

      {/* Assignment mode banner */}
      {assignmentMode.active && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium">
          Assignment Mode — Click a transformer
        </div>
      )}

      {/* Assign/undo failures. NOT gated on assignmentMode: an undo fails long
          after assignment mode ended, and a banner that cannot render is the
          same as no error at all. Reviewer-flagged collision fix: this used to
          share top-16 with the transient `notice` banner below, so one could
          paint over the other; top-28 is its own tier, clear of every other
          top-anchored banner (top-3 mode/prompt tier, top-16 notice, top-40
          undo). Manually dismissible (an × control, not a timer) — an error
          the operator must act on should not vanish on its own; that is
          exactly the silent-failure bug this task exists to close. */}
      {assignError && (
        <div className="absolute top-28 left-1/2 -translate-x-1/2 z-[1000] bg-red-600 text-white pl-4 pr-2 py-2 rounded-lg shadow-lg text-sm font-medium flex items-center gap-3">
          <span>{assignError}</span>
          <button
            onClick={() => setAssignError(null)}
            aria-label="Dismiss"
            className="px-1.5 py-0.5 rounded hover:bg-white/20 leading-none"
          >
            ×
          </button>
        </div>
      )}

      {/* 30 s undo after a successful reassign (UX C4). Reviewer-flagged
          collision fix: this used to share top-3 with the assignment-mode
          banner AND the voltage-drop/fault-current feeder-selection prompts
          (all four guard on !assignmentMode.active except the mode banner
          itself, so undo + a layer prompt could render at the same spot at
          once); top-40 is its own tier. */}
      {!assignmentMode.active && pendingUndo &&
        hasCapability(user?.capabilities, 'gis.override') && (
        <div className="absolute top-40 left-1/2 -translate-x-1/2 z-[1000] bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium flex items-center gap-3">
          <span>Meter {pendingUndo.meterNumber ?? `#${pendingUndo.meterId}`} reassigned.</span>
          <button
            onClick={handleUndoAssign}
            disabled={undoBusy}
            className="underline hover:no-underline disabled:opacity-50 disabled:no-underline"
          >
            {undoBusy ? 'Undoing…' : 'Undo'}
          </button>
        </div>
      )}

      {downstreamHighlight && downstreamHighlight.features.length > 0 && (
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-[1000] bg-amber-500 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium flex items-center gap-3">
          Showing {downstreamHighlight.features.length} downstream member(s)
          <button
            onClick={onClearDownstream}
            className="px-2 py-0.5 text-xs bg-white/20 rounded hover:bg-white/30"
          >
            Clear
          </button>
        </div>
      )}

      {/* Voltage drop feeder selection prompt */}
      {visibleLayers.voltageDrop && !selectedFeederId && !assignmentMode.active && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] bg-amber-500 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium">
          Click a feeder on the map to view voltage drop
        </div>
      )}

      {/* Fault current feeder selection prompt */}
      {visibleLayers.faultCurrent && !selectedFeederId && !assignmentMode.active && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] bg-amber-500 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium">
          Click a feeder on the map to view fault current
        </div>
      )}

      {/* Transient notice banner (e.g. search result without geometry).
          top-16 (below the top-3 feeder-selection prompts) so the banners never stack. */}
      {notice && !assignmentMode.active && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-[1000] bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium">
          {notice}
        </div>
      )}

      {/* Zoom-gate prompt: heavy layers (transformers, meters, devices,
          secondary) only load once zoomed into a working area (spec D-9).
          Lists whichever gated layers are toggled on. NOT guarded on
          assignmentMode — at low zoom the "Click a transformer" assignment
          prompt would otherwise sit over an empty map with no explanation. */}
      {zoomGateHint.length > 0 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[1000] bg-purple-700 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium">
          Zoom in to load {zoomGateHint.join(', ')}
        </div>
      )}

      {/* Layer control overlay */}
      <LayerControl
        visibleLayers={visibleLayers}
        onToggleLayer={toggleLayer}
        layerCounts={{
          substations: (substations as GeoJSONFeatureCollection)?.features?.length,
          feeders: (feeders as GeoJSONFeatureCollection)?.features?.length,
          // Transformers/devices query keys don't change with the zoom gate
          // (only the `enabled` option does), so react-query keeps serving
          // the last-fetched data for that key even while the query is
          // disabled — gate the displayed count directly on detailZoomOk so
          // the badge doesn't claim a count for a layer drawing nothing.
          transformers: detailZoomOk
            ? (transformers as GeoJSONFeatureCollection)?.features?.length
            : undefined,
          meters: (meters as GeoJSONFeatureCollection)?.features?.length,
          conductors: (conductors as GeoJSONFeatureCollection)?.features?.length,
          secondary: (secondary as GeoJSONFeatureCollection)?.features?.length,
          devices: detailZoomOk
            ? (devices as GeoJSONFeatureCollection)?.features?.length
            : undefined,
          // The badge must count what's actually drawn (house rule: never
          // render a silently-partial layer as complete — see LayerControl's
          // TruncationNote), so this reads the filtered effectiveLoadingLayer,
          // not the raw loadingLayer.
          loading: (effectiveLoadingLayer as GeoJSONFeatureCollection)?.features?.length,
          voltageDrop: (vdropLayer as GeoJSONFeatureCollection)?.features?.length,
          faultCurrent: (faultLayer as GeoJSONFeatureCollection)?.features?.length,
          measuredLoading: (measuredLoadingLayer as GeoJSONFeatureCollection)?.features?.length,
          voltageViolations: (violationsLayer as GeoJSONFeatureCollection)?.features?.length,
        }}
        layerTotals={{
          meters: (meters as GeoJSONFeatureCollection)?.total_count ?? undefined,
          conductors: (conductors as GeoJSONFeatureCollection)?.total_count ?? undefined,
          // Same staleness guard as the devices count above — the truncation
          // note ("N of M shown") must not survive the gate either.
          devices: detailZoomOk
            ? (devices as GeoJSONFeatureCollection)?.total_count ?? undefined
            : undefined,
        }}
        layerLoading={{
          substations: loadingSubstations,
          feeders: loadingFeeders,
          transformers: loadingTransformers,
          meters: loadingMeters,
          conductors: loadingConductors,
          secondary: loadingSecondary,
          devices: loadingDevices,
          loading: loadingLoadingLayer,
          voltageDrop: loadingVdropLayer,
          faultCurrent: loadingFaultLayer,
          measuredLoading: loadingMeasuredLoadingLayer,
          voltageViolations: loadingViolationsLayer,
        }}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        vdropNeedsFeeder={!selectedFeederId}
        faultNeedsFeeder={!selectedFeederId}
        faultIndicative={faultIndicative}
        secondaryColorByConfidence={secondaryColorByConfidence}
        onToggleSecondaryColorByConfidence={() => setSecondaryColorByConfidence(v => !v)}
      />
    </div>
  )
}
